import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  collection,
  query,
  orderBy,
  onSnapshot,
  doc,
  updateDoc,
  getDoc,
  deleteDoc,
  Timestamp,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { usePermissions } from '../hooks/usePermissions';
import { useAuth } from '../hooks/useAuth';
import { logAdminActivity } from '../services/adminActivityLog';
import {
  TrashIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  UserIcon,
  EnvelopeIcon,
  PhoneIcon,
  CurrencyDollarIcon,
  CalendarIcon,
  ShieldExclamationIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ExclamationTriangleIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline';

const ITEMS_PER_PAGE = 20;

export default function DeleteRequests() {
  const { hasPermission } = usePermissions();
  const { user } = useAuth();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [selectedRequest, setSelectedRequest] = useState(null);
  // Mirror of selectedRequest used inside the Firestore subscription so the
  // subscription doesn't need selectedRequest as a dependency (which caused
  // the modal to reopen itself in a loop when closing).
  const selectedRequestRef = useRef(null);
  useEffect(() => { selectedRequestRef.current = selectedRequest; }, [selectedRequest]);
  const [userDetails, setUserDetails] = useState({});
  const [processingId, setProcessingId] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [userDetailsCache, setUserDetailsCache] = useState({});

  // Fetch user details function with caching
  const fetchUserDetails = useCallback(async (userId) => {
    if (userDetailsCache[userId]) {
      return userDetailsCache[userId];
    }
    
    try {
      const userDoc = await getDoc(doc(db, 'users', userId));
      if (userDoc.exists()) {
        const userData = userDoc.data();
        setUserDetailsCache(prev => ({ ...prev, [userId]: userData }));
        return userData;
      }
    } catch (error) {
      console.error('Error fetching user details:', error);
    }
    return null;
  }, [userDetailsCache]);

  // Refresh user details for selected request
  const refreshUserDetails = async (userId) => {
    try {
      const userDoc = await getDoc(doc(db, 'users', userId));
      if (userDoc.exists()) {
        const userData = userDoc.data();
        setUserDetailsCache(prev => ({ ...prev, [userId]: userData }));
        setUserDetails(prev => ({ ...prev, [userId]: userData }));
        return userData;
      }
    } catch (error) {
      console.error('Error refreshing user details:', error);
    }
    return null;
  };

  useEffect(() => {
    if (!hasPermission('view_delete_requests')) {
      return;
    }

    // No server-side orderBy — Firestore excludes docs that are missing
    // the field, which was hiding accepted/rejected requests whose
    // createdAt was stripped by the DeleteUser Cloud Function.
    const q = query(collection(db, 'deleteRequests'));

    const unsubscribe = onSnapshot(
      q,
      async (snapshot) => {
        const requestsData = [];
        const userIds = new Set();

        snapshot.forEach((doc) => {
          const data = { id: doc.id, ...doc.data() };
          requestsData.push(data);
          userIds.add(data.userId);
        });

        // Sort client-side: newest first by createdAt (or updatedAt or
        // acceptedAt as a fallback). Docs with no timestamp at all sink to
        // the bottom but they still appear.
        const tsMillis = (r) => {
          const t = r.createdAt || r.updatedAt || r.acceptedAt || r.rejectedAt;
          if (!t) return 0;
          if (typeof t.toMillis === 'function') return t.toMillis();
          if (typeof t === 'number') return t;
          if (typeof t === 'string') return new Date(t).getTime() || 0;
          return 0;
        };
        requestsData.sort((a, b) => tsMillis(b) - tsMillis(a));

        setRequests(requestsData);

        // Fetch user details for all users
        const detailsPromises = Array.from(userIds).map(async (userId) => {
          const userData = await fetchUserDetails(userId);
          return { [userId]: userData };
        });

        const detailsArray = await Promise.all(detailsPromises);
        const detailsMap = Object.assign({}, ...detailsArray);
        setUserDetails(detailsMap);

        // Auto-refresh selected request if it's updated.
        // Read via ref so this callback (and its parent effect) doesn't
        // depend on selectedRequest — that dependency was causing a
        // re-subscribe loop that reopened the closed modal.
        const current = selectedRequestRef.current;
        if (current) {
          const updatedRequest = requestsData.find(r => r.id === current.id);
          if (updatedRequest) {
            setSelectedRequest(updatedRequest);
          }
        }

        setLoading(false);
      },
      (error) => {
        console.error('Error fetching delete requests:', error);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [hasPermission, fetchUserDetails]);

  const handleUpdateStatus = async (requestId, newStatus) => {
    if (!hasPermission('manage_delete_requests')) {
      alert('You do not have permission to update request status');
      return;
    }

    const request = requests.find(r => r.id === requestId);
    if (!request) {
      alert('Request not found');
      return;
    }

    const userData = userDetails[request.userId];
    const userType = userData?.type || 'unknown';

    const confirmMessage = newStatus === 'accepted' 
      ? `⚠️ CONFIRM ACCOUNT DELETION ⚠️\n\nUser: ${request.userName}\nType: ${userType.toUpperCase()}\nPhone: ${request.phoneNumber}\n\nThis will PERMANENTLY:\n✓ Delete Firebase Authentication\n✓ Delete all ${userType === 'driver' ? 'wallet transactions & ratings' : 'addresses & ratings'}\n✓ Remove blocked number entries (if banned)\n✓ Replace user data with "Deleted Account"\n\n⚠️ THIS ACTION CANNOT BE UNDONE!\n\nType "DELETE" to confirm:`
      : `Are you sure you want to REJECT the deletion request for ${request.userName}?`;

    if (newStatus === 'accepted') {
      const userConfirmation = window.prompt(confirmMessage);
      if (userConfirmation !== 'DELETE') {
        alert('Deletion cancelled. You must type "DELETE" to confirm.');
        return;
      }
    } else {
      if (!window.confirm(confirmMessage)) {
        return;
      }
    }

    setProcessingId(requestId);
    try {
      // Update the request status - Cloud Function will handle the deletion
      const requestRef = doc(db, 'deleteRequests', requestId);
      await updateDoc(requestRef, {
        status: newStatus,
        updatedAt: Timestamp.now(),
        processedBy: user.uid,
        processedByName: user.name || user.email,
        processedAt: Timestamp.now(),
      });

      logAdminActivity({
        actor: user,
        action: newStatus === 'accepted' ? 'approve_delete_request' : 'reject_delete_request',
        targetType: 'delete_request',
        targetId: requestId,
        targetLabel: `${request.userName} · ${request.phoneNumber || ''}`.trim(),
      });
      if (newStatus === 'accepted') {
        alert('✅ Deletion request accepted!\n\nThe Cloud Function "DeleteUser" is now processing the account deletion.\n\nThis includes:\n• Deleting all user data\n• Removing subcollections\n• Deleting authentication\n• Replacing with "Deleted Account"\n\nThe modal will stay open. You can refresh to see the deletion status.');
      } else {
        alert('Request rejected successfully');
        setSelectedRequest(null);
      }
    } catch (error) {
      console.error('Error updating request status:', error);
      alert('An error occurred while updating request status: ' + error.message);
    } finally {
      setProcessingId(null);
    }
  };

  const handleDeleteRequest = async (requestId) => {
    if (!hasPermission('manage_delete_requests')) {
      alert('You do not have permission to delete this request');
      return;
    }

    if (!window.confirm('Are you sure you want to permanently delete this deletion request record?\n\nNote: This only deletes the request record, not the user account.')) {
      return;
    }

    setProcessingId(requestId);
    try {
      await deleteDoc(doc(db, 'deleteRequests', requestId));
      alert('Request record deleted successfully');
      setSelectedRequest(null);
    } catch (error) {
      console.error('Error deleting request:', error);
      alert('An error occurred while deleting the request');
    } finally {
      setProcessingId(null);
    }
  };

  const handleRefreshStatus = async () => {
    if (!selectedRequest) return;
    
    setProcessingId(selectedRequest.id);
    try {
      // Refresh the request document
      const requestDoc = await getDoc(doc(db, 'deleteRequests', selectedRequest.id));
      if (requestDoc.exists()) {
        setSelectedRequest({ id: requestDoc.id, ...requestDoc.data() });
      }
      
      // Refresh user details
      await refreshUserDetails(selectedRequest.userId);
      
      alert('Status refreshed successfully');
    } catch (error) {
      console.error('Error refreshing status:', error);
      alert('Error refreshing status: ' + error.message);
    } finally {
      setProcessingId(null);
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'Not specified';
    const date = timestamp.toDate();
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-yellow-100 text-yellow-800',
      accepted: 'bg-green-100 text-green-800',
      rejected: 'bg-red-100 text-red-800',
    };

    const labels = {
      pending: 'Pending',
      accepted: 'Accepted',
      rejected: 'Rejected',
    };

    return (
      <span
        className={`px-3 py-1 rounded-full text-sm font-medium ${styles[status]}`}
      >
        {labels[status]}
      </span>
    );
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending':
        return <ClockIcon className="h-5 w-5 text-yellow-600" />;
      case 'accepted':
        return <CheckCircleIcon className="h-5 w-5 text-green-600" />;
      case 'rejected':
        return <XCircleIcon className="h-5 w-5 text-red-600" />;
      default:
        return null;
    }
  };

  const filteredRequests = useMemo(() => {
    return requests.filter((req) => {
      if (filter === 'all') return true;
      return req.status === filter;
    });
  }, [requests, filter]);

  // Pagination logic
  const totalPages = Math.ceil(filteredRequests.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;
  const paginatedRequests = filteredRequests.slice(startIndex, endIndex);

  // Reset to page 1 when filter changes
  useEffect(() => {
    setCurrentPage(1);
  }, [filter]);

  const stats = useMemo(() => ({
    total: requests.length,
    pending: requests.filter((r) => r.status === 'pending').length,
    accepted: requests.filter((r) => r.status === 'accepted').length,
    rejected: requests.filter((r) => r.status === 'rejected').length,
  }), [requests]);

  if (!hasPermission('view_delete_requests')) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <ShieldExclamationIcon className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">
            Not Authorized
          </h3>
          <p className="mt-1 text-sm text-gray-500">
            You do not have permission to access this page
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4">
      {/* Header */}
      <div className="mb-4">
        <h1 className="text-2xl font-bold text-gray-900">
          Account Deletion Requests
        </h1>
        <p className="mt-1 text-sm text-gray-600">
          Manage user account deletion requests - Processed by Cloud Function "DeleteUser"
        </p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-4">
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Requests</p>
              <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
            </div>
            <TrashIcon className="h-8 w-8 text-gray-400" />
          </div>
        </div>

        <div className="bg-white p-4 rounded-lg shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Pending</p>
              <p className="text-2xl font-bold text-yellow-600">
                {stats.pending}
              </p>
            </div>
            <ClockIcon className="h-8 w-8 text-yellow-400" />
          </div>
        </div>

        <div className="bg-white p-4 rounded-lg shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Accepted</p>
              <p className="text-2xl font-bold text-green-600">
                {stats.accepted}
              </p>
            </div>
            <CheckCircleIcon className="h-8 w-8 text-green-400" />
          </div>
        </div>

        <div className="bg-white p-4 rounded-lg shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Rejected</p>
              <p className="text-2xl font-bold text-red-600">
                {stats.rejected}
              </p>
            </div>
            <XCircleIcon className="h-8 w-8 text-red-400" />
          </div>
        </div>
      </div>

      {/* Filter Tabs */}
      <div className="mb-4 bg-white rounded-lg shadow">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8 px-4" aria-label="Tabs">
            {[
              { key: 'all', label: 'All', count: stats.total },
              { key: 'pending', label: 'Pending', count: stats.pending },
              { key: 'accepted', label: 'Accepted', count: stats.accepted },
              { key: 'rejected', label: 'Rejected', count: stats.rejected },
            ].map((tab) => (
              <button
                key={tab.key}
                onClick={() => setFilter(tab.key)}
                className={`${
                  filter === tab.key
                    ? 'border-yellow-500 text-yellow-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                } whitespace-nowrap py-3 px-1 border-b-2 font-medium text-sm`}
              >
                {tab.label}
                <span
                  className={`${
                    filter === tab.key
                      ? 'bg-yellow-100 text-yellow-600'
                      : 'bg-gray-100 text-gray-900'
                  } ml-3 py-0.5 px-2.5 rounded-full text-xs font-medium`}
                >
                  {tab.count}
                </span>
              </button>
            ))}
          </nav>
        </div>
      </div>

      {/* Requests List */}
      {loading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-500"></div>
        </div>
      ) : filteredRequests.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-12 text-center">
          <TrashIcon className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">
            No Requests
          </h3>
          <p className="mt-1 text-sm text-gray-500">
            There are no account deletion requests in this category
          </p>
        </div>
      ) : (
        <>
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    User
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Request Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {paginatedRequests.map((request) => {
                  const userData = userDetails[request.userId];
                  const isDeleted = userData?.status === 'deleted';
                  
                  return (
                    <tr
                      key={request.id}
                      className="hover:bg-gray-50 cursor-pointer"
                      onClick={() => setSelectedRequest(request)}
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          {userData?.profilePhoto && !isDeleted ? (
                            <img
                              src={userData.profilePhoto}
                              alt={request.userName}
                              className="h-10 w-10 rounded-full object-cover"
                            />
                          ) : (
                            <div className={`h-10 w-10 rounded-full ${isDeleted ? 'bg-red-200' : 'bg-gray-200'} flex items-center justify-center`}>
                              <UserIcon className={`h-6 w-6 ${isDeleted ? 'text-red-400' : 'text-gray-400'}`} />
                            </div>
                          )}
                          <div className="ml-4">
                            <div className={`text-sm font-medium ${isDeleted ? 'text-red-900' : 'text-gray-900'}`}>
                              {isDeleted ? 'Deleted Account' : request.userName}
                              {isDeleted && <span className="ml-2 text-xs text-red-600">(Deleted)</span>}
                            </div>
                            <div className="text-sm text-gray-500">
                              {isDeleted ? 'Deleted' : request.phoneNumber}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                            userData?.type === 'driver'
                              ? 'bg-blue-100 text-blue-800'
                              : 'bg-purple-100 text-purple-800'
                          }`}
                        >
                          {userData?.type === 'driver' ? 'Driver' : 'Customer'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatDate(request.createdAt)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          {/* Show appropriate icon based on status and deletion state */}
                          {request.status === 'accepted' && request.deletionComplete ? (
                            <>
                              <CheckCircleIcon className="h-5 w-5 text-green-600" />
                              <span className="px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
                                Completed
                              </span>
                            </>
                          ) : request.status === 'accepted' && !request.deletionComplete ? (
                            <>
                              <ArrowPathIcon className="h-5 w-5 text-blue-600 animate-spin" />
                              <span className="px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                                Processing
                              </span>
                            </>
                          ) : (
                            <>
                              {getStatusIcon(request.status)}
                              {getStatusBadge(request.status)}
                            </>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedRequest(request);
                          }}
                          className="text-yellow-600 hover:text-yellow-900"
                        >
                          View Details
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6 mt-4 rounded-lg shadow">
              <div className="flex-1 flex justify-between sm:hidden">
                <button
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                  className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                <button
                  onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                  disabled={currentPage === totalPages}
                  className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
              <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm text-gray-700">
                    Showing <span className="font-medium">{startIndex + 1}</span> to{' '}
                    <span className="font-medium">{Math.min(endIndex, filteredRequests.length)}</span> of{' '}
                    <span className="font-medium">{filteredRequests.length}</span> results
                  </p>
                </div>
                <div>
                  <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                    <button
                      onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                      disabled={currentPage === 1}
                      className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <span className="sr-only">Previous</span>
                      <ChevronLeftIcon className="h-5 w-5" aria-hidden="true" />
                    </button>
                    
                    {[...Array(totalPages)].map((_, idx) => {
                      const pageNum = idx + 1;
                      if (
                        pageNum === 1 ||
                        pageNum === totalPages ||
                        (pageNum >= currentPage - 1 && pageNum <= currentPage + 1)
                      ) {
                        return (
                          <button
                            key={pageNum}
                            onClick={() => setCurrentPage(pageNum)}
                            className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                              currentPage === pageNum
                                ? 'z-10 bg-yellow-50 border-yellow-500 text-yellow-600'
                                : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                            }`}
                          >
                            {pageNum}
                          </button>
                        );
                      } else if (
                        pageNum === currentPage - 2 ||
                        pageNum === currentPage + 2
                      ) {
                        return (
                          <span
                            key={pageNum}
                            className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700"
                          >
                            ...
                          </span>
                        );
                      }
                      return null;
                    })}
                    
                    <button
                      onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                      disabled={currentPage === totalPages}
                      className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <span className="sr-only">Next</span>
                      <ChevronRightIcon className="h-5 w-5" aria-hidden="true" />
                    </button>
                  </nav>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {/* Details Modal */}
      {selectedRequest && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-3xl shadow-lg rounded-md bg-white">
            <div className="flex justify-between items-start mb-4">
              <h3 className="text-2xl font-bold text-gray-900">
                Delete Request Details
              </h3>
              <div className="flex gap-2">
                <button
                  onClick={handleRefreshStatus}
                  disabled={processingId === selectedRequest.id}
                  className="text-blue-600 hover:text-blue-800 disabled:opacity-50"
                  title="Refresh status"
                >
                  <ArrowPathIcon className={`h-6 w-6 ${processingId === selectedRequest.id ? 'animate-spin' : ''}`} />
                </button>
                <button
                  onClick={() => setSelectedRequest(null)}
                  className="text-gray-400 hover:text-gray-500"
                >
                  <XCircleIcon className="h-6 w-6" />
                </button>
              </div>
            </div>

            <div className="space-y-6">
              {/* Deletion Status */}
              {selectedRequest.status === 'accepted' && (
                <div className={`p-4 rounded-lg ${selectedRequest.deletionComplete ? 'bg-green-50 border border-green-200' : 'bg-blue-50 border border-blue-200'}`}>
                  <div className="flex items-start gap-3">
                    {selectedRequest.deletionComplete ? (
                      <CheckCircleIcon className="h-6 w-6 text-green-600 flex-shrink-0 mt-0.5" />
                    ) : (
                      <ClockIcon className="h-6 w-6 text-blue-600 flex-shrink-0 mt-0.5" />
                    )}
                    <div className="flex-1">
                      <h4 className={`font-semibold ${selectedRequest.deletionComplete ? 'text-green-900' : 'text-blue-900'}`}>
                        {selectedRequest.deletionComplete ? '✅ Deletion Completed' : '⏳ Deletion In Progress'}
                      </h4>
                      <p className={`text-sm mt-1 ${selectedRequest.deletionComplete ? 'text-green-700' : 'text-blue-700'}`}>
                        {selectedRequest.deletionComplete 
                          ? 'The Cloud Function has successfully deleted this account and all related data.'
                          : 'The Cloud Function "DeleteUser" is processing this deletion request. This may take a few seconds. Click the refresh button above to update.'}
                      </p>
                      {selectedRequest.deletionDetails && (
                        <div className="mt-2 p-2 bg-white rounded text-xs text-gray-700">
                          <p><strong>Original Name:</strong> {selectedRequest.deletionDetails.originalName}</p>
                          <p><strong>User Type:</strong> {selectedRequest.deletionDetails.userType}</p>
                          <p><strong>Deleted At:</strong> {formatDate(selectedRequest.deletionDetails.timestamp)}</p>
                        </div>
                      )}
                      {selectedRequest.deletionError && (
                        <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded">
                          <p className="text-xs text-red-700">
                            <ExclamationTriangleIcon className="h-4 w-4 inline mr-1" />
                            <strong>Error:</strong> {selectedRequest.deletionError}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* User Profile Section */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="text-lg font-semibold mb-3 text-gray-900">
                  User Information
                </h4>
                <div className="flex items-start gap-4">
                  {userDetails[selectedRequest.userId]?.profilePhoto && userDetails[selectedRequest.userId]?.status !== 'deleted' ? (
                    <img
                      src={userDetails[selectedRequest.userId].profilePhoto}
                      alt={selectedRequest.userName}
                      className="h-20 w-20 rounded-full object-cover"
                    />
                  ) : (
                    <div className={`h-20 w-20 rounded-full ${userDetails[selectedRequest.userId]?.status === 'deleted' ? 'bg-red-200' : 'bg-gray-200'} flex items-center justify-center`}>
                      <UserIcon className={`h-10 w-10 ${userDetails[selectedRequest.userId]?.status === 'deleted' ? 'text-red-400' : 'text-gray-400'}`} />
                    </div>
                  )}
                  <div className="flex-1 space-y-2">
                    <div className="flex items-center gap-2">
                      <UserIcon className="h-5 w-5 text-gray-400" />
                      <span className="text-sm text-gray-600">Name:</span>
                      <span className="text-sm font-medium text-gray-900">
                        {userDetails[selectedRequest.userId]?.status === 'deleted' ? 'Deleted Account' : selectedRequest.userName}
                      </span>
                      {userDetails[selectedRequest.userId]?.status === 'deleted' && (
                        <span className="text-xs bg-red-100 text-red-800 px-2 py-0.5 rounded-full">DELETED</span>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <PhoneIcon className="h-5 w-5 text-gray-400" />
                      <span className="text-sm text-gray-600">Phone:</span>
                      <span className="text-sm font-medium text-gray-900">
                        {userDetails[selectedRequest.userId]?.status === 'deleted' ? 'Deleted' : selectedRequest.phoneNumber}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <EnvelopeIcon className="h-5 w-5 text-gray-400" />
                      <span className="text-sm text-gray-600">Email:</span>
                      <span className="text-sm font-medium text-gray-900">
                        {userDetails[selectedRequest.userId]?.status === 'deleted' ? 'deleted@account.com' : (selectedRequest.email || 'Not specified')}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-gray-600">Type:</span>
                      <span
                        className={`px-2 py-1 text-xs font-semibold rounded-full ${
                          userDetails[selectedRequest.userId]?.type === 'driver'
                            ? 'bg-blue-100 text-blue-800'
                            : 'bg-purple-100 text-purple-800'
                        }`}
                      >
                        {userDetails[selectedRequest.userId]?.type === 'driver'
                          ? 'Driver'
                          : 'Customer'}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-gray-600">Status:</span>
                      <span
                        className={`px-2 py-1 text-xs font-semibold rounded-full ${
                          userDetails[selectedRequest.userId]?.status === 'deleted'
                            ? 'bg-red-100 text-red-800'
                            : userDetails[selectedRequest.userId]?.status === 'active'
                            ? 'bg-green-100 text-green-800'
                            : 'bg-gray-100 text-gray-800'
                        }`}
                      >
                        {userDetails[selectedRequest.userId]?.status === 'deleted'
                          ? 'Deleted'
                          : userDetails[selectedRequest.userId]?.status === 'active'
                          ? 'Active'
                          : userDetails[selectedRequest.userId]?.status === 'banned'
                          ? 'Banned'
                          : 'Inactive'}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Driver-specific information */}
                {userDetails[selectedRequest.userId]?.type === 'driver' && userDetails[selectedRequest.userId]?.status !== 'deleted' && (
                  <div className="mt-4 pt-4 border-t border-gray-200">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="flex items-center gap-2">
                        <CurrencyDollarIcon className="h-5 w-5 text-gray-400" />
                        <span className="text-sm text-gray-600">Balance:</span>
                        <span className="text-sm font-bold text-gray-900">
                          {userDetails[selectedRequest.userId]?.wallet_balance?.toFixed(
                            2
                          ) || '0.00'}{' '}
                          JOD
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-gray-600">
                          Orders Delivered:
                        </span>
                        <span className="text-sm font-bold text-gray-900">
                          {userDetails[selectedRequest.userId]
                            ?.total_orders_delivered || 0}
                        </span>
                      </div>
                    </div>
                  </div>
                )}

                {/* Deleted account info */}
                {userDetails[selectedRequest.userId]?.status === 'deleted' && userDetails[selectedRequest.userId]?.deletedAt && (
                  <div className="mt-4 pt-4 border-t border-gray-200">
                    <div className="flex items-center gap-2 text-red-600">
                      <ExclamationTriangleIcon className="h-5 w-5" />
                      <span className="text-sm font-medium">
                        Account Deleted: {formatDate(userDetails[selectedRequest.userId].deletedAt)}
                      </span>
                    </div>
                    <p className="text-xs text-gray-600 mt-1">
                      All user data has been removed and replaced with "Deleted Account"
                    </p>
                  </div>
                )}

                {/* Banned info */}
                {userDetails[selectedRequest.userId]?.status === 'banned' &&
                  userDetails[selectedRequest.userId]?.bannedAt && (
                    <div className="mt-4 pt-4 border-t border-gray-200">
                      <div className="flex items-center gap-2 text-orange-600">
                        <ShieldExclamationIcon className="h-5 w-5" />
                        <span className="text-sm font-medium">
                          Banned since:{' '}
                          {formatDate(
                            userDetails[selectedRequest.userId].bannedAt
                          )}
                        </span>
                      </div>
                    </div>
                  )}

                {/* Account creation date */}
                {userDetails[selectedRequest.userId]?.createdAt && (
                  <div className="mt-4 pt-4 border-t border-gray-200">
                    <div className="flex items-center gap-2">
                      <CalendarIcon className="h-5 w-5 text-gray-400" />
                      <span className="text-sm text-gray-600">
                        Account Created:
                      </span>
                      <span className="text-sm font-medium text-gray-900">
                        {formatDate(userDetails[selectedRequest.userId].createdAt)}
                      </span>
                    </div>
                  </div>
                )}
              </div>

              {/* Request Details */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="text-lg font-semibold mb-3 text-gray-900">
                  Request Details
                </h4>
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <CalendarIcon className="h-5 w-5 text-gray-400" />
                    <span className="text-sm text-gray-600">Request Date:</span>
                    <span className="text-sm font-medium text-gray-900">
                      {formatDate(selectedRequest.createdAt)}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-600">Status:</span>
                    {getStatusBadge(selectedRequest.status)}
                  </div>
                  {selectedRequest.processedAt && (
                    <>
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-gray-600">
                          Processed By:
                        </span>
                        <span className="text-sm font-medium text-gray-900">
                          {selectedRequest.processedByName || 'Not specified'}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-gray-600">
                          Processing Date:
                        </span>
                        <span className="text-sm font-medium text-gray-900">
                          {formatDate(selectedRequest.processedAt)}
                        </span>
                      </div>
                    </>
                  )}
                  {selectedRequest.deletedAt && (
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-gray-600">
                        Deletion Completed:
                      </span>
                      <span className="text-sm font-medium text-green-900">
                        {formatDate(selectedRequest.deletedAt)}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {/* Actions */}
              {selectedRequest.status === 'pending' &&
                hasPermission('manage_delete_requests') && (
                  <div className="flex gap-3">
                    <button
                      onClick={() =>
                        handleUpdateStatus(selectedRequest.id, 'accepted')
                      }
                      disabled={processingId === selectedRequest.id}
                      className="flex-1 bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    >
                      <CheckCircleIcon className="h-5 w-5" />
                      Accept & Delete Account
                    </button>
                    <button
                      onClick={() =>
                        handleUpdateStatus(selectedRequest.id, 'rejected')
                      }
                      disabled={processingId === selectedRequest.id}
                      className="flex-1 bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    >
                      <XCircleIcon className="h-5 w-5" />
                      Reject Request
                    </button>
                  </div>
                )}

              {hasPermission('manage_delete_requests') && (
                <div className="border-t pt-4">
                  <button
                    onClick={() => handleDeleteRequest(selectedRequest.id)}
                    disabled={processingId === selectedRequest.id}
                    className="w-full bg-gray-600 text-white px-4 py-2 rounded-md hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    <TrashIcon className="h-5 w-5" />
                    Delete Request Record
                  </button>
                  <p className="text-xs text-gray-500 mt-2 text-center">
                    This only deletes the request record, not the user account
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}